import React from 'react';

export const Logo = ({ size = 'default', className = '', darkMode = false }) => {
  const dimensions = {
    small: { width: 56, height: 56 },
    default: { width: 72, height: 72 },
    large: { width: 96, height: 96 }
  };

  const { width, height } = dimensions[size] || dimensions.default;
  
  // Luxury Gold Theme
  const primaryGold = '#D4AF37';
  const darkGold = '#B8960F';
  const lightGold = '#F4D03F';
  const deepBlack = '#000000';
  const pureWhite = '#FFFFFF';
  
  const mainColor = darkMode ? pureWhite : deepBlack;
  const bgColor = darkMode ? deepBlack : pureWhite;

  return (
    <div className={`relative ${className}`} style={{ width, height }}>
      <svg
        width={width}
        height={height}
        viewBox="0 0 100 100"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <defs>
          <linearGradient id="goldGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={lightGold} />
            <stop offset="50%" stopColor={primaryGold} />
            <stop offset="100%" stopColor={darkGold} />
          </linearGradient>
          <linearGradient id="radialGold" x1="50%" y1="0%" x2="50%" y2="100%">
            <stop offset="0%" stopColor={lightGold} stopOpacity="0.9" />
            <stop offset="100%" stopColor={darkGold} stopOpacity="1" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        
        {/* Outer decorative ring */}
        <circle
          cx="50"
          cy="50"
          r="46"
          fill="none"
          stroke="url(#goldGradient)"
          strokeWidth="2"
          opacity="0.4"
        />
        
        {/* Main golden badge with elegant shape */}
        <path
          d="M50,8 L62,18 L76,20 L80,34 L74,46 L80,58 L76,72 L62,74 L50,84 L38,74 L24,72 L20,58 L26,46 L20,34 L24,20 L38,18 Z"
          fill="url(#radialGold)"
          filter="url(#glow)"
        />
        
        {/* Elegant scissors design - crossed in luxury style */}
        <g transform="translate(50, 50)">
          {/* Left scissor blade */}
          <path
            d="M-8,-20 L-12,-8 L-4,8"
            stroke={mainColor}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          {/* Right scissor blade */}
          <path
            d="M8,-20 L12,-8 L4,8"
            stroke={mainColor}
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            fill="none"
          />
          {/* Pivot point */}
          <circle cx="0" cy="-8" r="3" fill={mainColor} />
          
          {/* Elegant handle rings */}
          <circle cx="-8" cy="-22" r="4" stroke={mainColor} strokeWidth="2.5" fill="none" />
          <circle cx="8" cy="-22" r="4" stroke={mainColor} strokeWidth="2.5" fill="none" />
          
          {/* Sharp blade tips with sparkle effect */}
          <circle cx="-4" cy="8" r="2" fill={mainColor} />
          <circle cx="4" cy="8" r="2" fill={mainColor} />
        </g>
        
        {/* Decorative sparkles around the badge */}
        <g opacity="0.8">
          {/* Top sparkle */}
          <path d="M50,2 L51,6 L50,10 L49,6 Z" fill="url(#goldGradient)" />
          <path d="M46,4 L50,5 L54,4 L50,3 Z" fill="url(#goldGradient)" />
          
          {/* Right sparkle */}
          <path d="M94,50 L90,51 L86,50 L90,49 Z" fill="url(#goldGradient)" />
          <path d="M92,46 L93,50 L92,54 L91,50 Z" fill="url(#goldGradient)" />
          
          {/* Bottom sparkle */}
          <path d="M50,90 L51,94 L50,98 L49,94 Z" fill="url(#goldGradient)" />
          <path d="M46,92 L50,93 L54,92 L50,91 Z" fill="url(#goldGradient)" />
          
          {/* Left sparkle */}
          <path d="M6,50 L10,51 L14,50 L10,49 Z" fill="url(#goldGradient)" />
          <path d="M8,46 L9,50 L8,54 L7,50 Z" fill="url(#goldGradient)" />
        </g>
        
        {/* Inner decorative mini stars */}
        <circle cx="30" cy="30" r="1.5" fill={lightGold} opacity="0.6" />
        <circle cx="70" cy="30" r="1.5" fill={lightGold} opacity="0.6" />
        <circle cx="30" cy="70" r="1.5" fill={lightGold} opacity="0.6" />
        <circle cx="70" cy="70" r="1.5" fill={lightGold} opacity="0.6" />
      </svg>
    </div>
  );
};

export const LogoWithText = ({ size = 'default', layout = 'horizontal', darkMode = false }) => {
  const textSizes = {
    small: { title: 'text-lg' },
    default: { title: 'text-2xl' },
    large: { title: 'text-4xl' }
  };

  const { title } = textSizes[size] || textSizes.default;
  const textColor = darkMode ? 'text-white' : 'text-gray-900';

  if (layout === 'vertical') {
    return (
      <div className="flex flex-col items-center space-y-4">
        <Logo size={size} darkMode={darkMode} />
        <div className="text-center">
          <div className={`font-black ${textColor} ${title} tracking-wide`} style={{ letterSpacing: '0.05em' }}>
            UNIVERSA SURGICAL
          </div>
          <div className="text-yellow-700 text-sm mt-1">
            Beauty & Salon Instruments
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center space-x-4">
      <Logo size={size} darkMode={darkMode} />
      <div className="flex flex-col justify-center">
        <span className={`font-black ${title} tracking-wide leading-none`} style={{ 
          color: darkMode ? '#ffffff' : '#000000',
          letterSpacing: '0.05em'
        }}>
          UNIVERSA SURGICAL
        </span>
        <span className="text-xs text-yellow-700 mt-1" style={{ 
          letterSpacing: '0.1em'
        }}>
          BEAUTY & SALON INSTRUMENTS
        </span>
      </div>
    </div>
  );
};
